import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { BootType, BootUser, setBootLoading, resetBoot } from "../../reducers/boot";
import { SetBootForCurrentBusiness } from "../../reducers/model/boot";
import { AboName, setAboName } from "../../reducers/aboName";
import { store } from "../../store";

export const deleteCookies = () => {
  document.cookie = `myBizUser=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
  document.cookie = `aboName=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
};

const deleteCache = () => {
  // The Cache Storage API isn't available in every environment (older browsers,
  // non-secure contexts, some test/SSR runtimes). Guard so session teardown
  // never throws now that this also runs at login start.
  if (typeof caches === "undefined") return;
  caches
    .keys()
    .then((keyList) => {
      return Promise.all(
        keyList.map((key) => {
          if (key.indexOf("$__acc") > -1) return caches.delete(key);
          return Promise.resolve();
        }),
      );
    })
    .catch(() => {
      console.warn("ACC-LOAD: Failed clearing cache storage.");
    });
};

const deleteRequestCache = () => {
  // indexedDB may be undefined/restricted in some environments; guard so
  // teardown doesn't throw a ReferenceError.
  if (typeof indexedDB === "undefined") return;
  const deleteDbRequestCache = indexedDB.deleteDatabase("RequestCache");
  deleteDbRequestCache.onerror = () => {
    console.warn("ACC-LOAD: Failed deleting RequestCache.");
  };
  deleteDbRequestCache.onsuccess = () => {
    console.log("ACC-LOAD: RequestCache deleted.");
  };
};

/**
 * Tears down all client-side session state: the in-memory redux boot slice,
 * the session cookie, persisted sessionStorage (boot.user), select localStorage
 * keys, the service-worker cache, and the RequestCache IndexedDB. Shared by
 * logout and by the start of every login so a stale session can never be
 * restored after a failed login.
 *
 * The in-memory `resetBoot()` dispatch is essential: PersistGate rehydrates the
 * boot slice from sessionStorage on page load, so clearing sessionStorage alone
 * is not enough — the next boot-mutating dispatch (e.g. setUser during login)
 * would otherwise re-persist the stale in-memory user straight back to storage.
 * Resetting the in-memory slice first means there is nothing stale to re-persist.
 *
 * Note: loginFields persist to localStorage and are intentionally preserved
 * here (only specific localStorage keys are removed), so fresh login fields set
 * immediately after this call survive a same-window navigation.
 */
export const clearSession = () => {
  // Reset the in-memory redux boot state before clearing storage, otherwise a
  // subsequent dispatch re-persists the stale rehydrated user (see above).
  store.dispatch(resetBoot());

  localStorage.removeItem("clientId");

  // chatbot local storage items
  localStorage.removeItem("chatter");
  localStorage.removeItem("cwr_c");
  localStorage.removeItem("cwr_i");
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("ada-")) {
      localStorage.removeItem(key);
    }
  });

  deleteCookies();
  deleteCache();
  deleteRequestCache();
  sessionStorage.clear();
};
// Custom hook to handle cookie logic
export const useHandleCookie = (
  boot: BootType | null = null,
  _locale: string | null = null,
  aboName: AboName | null = null,
) => {
  const dispatch = useDispatch();

  const setCookie = (user: BootUser, aboName: AboName) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + 60 * 60 * 1000); // Set expiration to 1 hour
    const expiresString = `expires=${expires.toUTCString()};path=/`;
    const userCookie = `myBizUser=${encodeURIComponent(JSON.stringify(user))};${expiresString}`;
    const aboNameCookie = `aboName=${encodeURIComponent(JSON.stringify(aboName))};${expiresString}`;

    if (user) {
      document.cookie = userCookie;
      document.cookie = aboNameCookie;
    }
  };

  const getUserCookie = (): BootUser | null => {
    const cookieName = "myBizUser=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(";");

    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.startsWith(cookieName)) {
        const cookieString = cookie.substring(cookieName.length);
        try {
          return JSON.parse(cookieString) as BootUser;
        } catch (error) {
          console.error("Error parsing BootUser cookie JSON:", error);
          return null;
        }
      }
    }
    return null;
  };

  const getAboCookie = (): AboName | null => {
    const cookieName = "aboName=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(";");

    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.startsWith(cookieName)) {
        const cookieString = cookie.substring(cookieName.length);
        try {
          return JSON.parse(cookieString) as AboName;
        } catch (error) {
          console.error("Error parsing AboName cookie JSON:", error);
          return null;
        }
      }
    }
    return null;
  };

  const isEmptyObject = (obj: object): boolean => {
    return Object.keys(obj).length === 0;
  };

  const handleCookieLogic = useCallback(async (): Promise<boolean> => {
    const user = getUserCookie();
    const abo = getAboCookie();

    if (boot === null) return false;

    if (!isEmptyObject(boot.configuration)) {
      if (!user) {
        if (aboName !== null) setCookie(boot.user, aboName);
      } else if (user.abo !== boot.user.abo || user.access_token !== boot.user.access_token) {
        if (aboName !== null) setCookie(boot.user, aboName);
      }
      return false;
    }

    if (!user?.access_token) return false;

    if (abo) {
      dispatch(setAboName(abo.name));
    }
    dispatch(setBootLoading(true));

    try {
      await SetBootForCurrentBusiness({ user });
      return true;
    } finally {
      dispatch(setBootLoading(false));
    }
  }, [aboName, boot, dispatch]);

  return { handleCookieLogic, deleteCookies };
};
